import { useCallback, useRef, useState } from 'react';
import { alpha, Box, Link, Typography, useTheme } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { AnimatePresence, motion } from 'framer-motion';

const EMPTY_DIGITS = ['', '', '', '', '', ''] as const;

type Props = {
  onComplete: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  label: string;
  isError: boolean;
  resendBusy: boolean;
  cooldownSecondsLeft?: number;
  resendLabel?: string;
};

export function AdminOTPInput({
  onComplete,
  onResend,
  label,
  isError,
  resendBusy,
  cooldownSecondsLeft = 0,
  resendLabel = 'Resend verification email',
}: Props) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const [digits, setDigits] = useState<string[]>([...EMPTY_DIGITS]);
  const [verified, setVerified] = useState(false);
  const [processing, setProcessing] = useState(false);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digitsRef = useRef<string[]>([...EMPTY_DIGITS]);
  const completionLockRef = useRef(false);

  const reset = useCallback(() => {
    digitsRef.current = [...EMPTY_DIGITS];
    completionLockRef.current = false;
    setDigits([...EMPTY_DIGITS]);
    setVerified(false);
    setProcessing(false);
    refs.current[0]?.focus();
  }, []);

  const tryComplete = useCallback(
    async (next: string[]) => {
      if (next.some((digit) => digit === '') || completionLockRef.current) {
        return;
      }

      completionLockRef.current = true;
      setProcessing(true);

      try {
        await onComplete(next.join(''));
        setVerified(true);
      } catch {
        completionLockRef.current = false;
        reset();
      } finally {
        setProcessing(false);
      }
    },
    [onComplete, reset],
  );

  const applyDigits = useCallback(
    (next: string[]) => {
      digitsRef.current = next;
      setDigits(next);
      if (next.every((digit) => digit !== '')) {
        void tryComplete(next);
      }
    },
    [tryComplete],
  );

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digitsRef.current];
    next[index] = digit;
    if (digit && index < 5) {
      setTimeout(() => refs.current[index + 1]?.focus(), 0);
    }
    applyDigits(next);
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const pasted = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    event.preventDefault();

    if (pasted.length !== 6 || completionLockRef.current) {
      return;
    }

    applyDigits(pasted.split(''));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1.5 }}>
        {digits.map((digit, index) => (
          <Box
            key={index}
            component="input"
            ref={(element: HTMLInputElement | null) => {
              refs.current[index] = element;
            }}
            value={digit}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(index, event.target.value)
            }
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Backspace' && !digits[index] && index > 0) {
                refs.current[index - 1]?.focus();
              }
            }}
            onPaste={handlePaste}
            maxLength={1}
            inputMode="numeric"
            disabled={verified || processing}
            autoFocus={index === 0}
            sx={{
              width: 44,
              height: 52,
              textAlign: 'center',
              fontSize: '1.4rem',
              fontWeight: 700,
              border: '2px solid',
              borderColor: isError ? theme.palette.error.main : digit ? theme.palette.primary.main : isLight ? '#E8EAED' : '#2E2E48',
              borderRadius: '12px',
              outline: 'none',
              background: verified ? alpha(theme.palette.success.main, 0.08) : isLight ? '#F8F9FF' : '#0D0D1A',
              color: theme.palette.text.primary,
              transition: 'all 0.2s',
              fontFamily: 'inherit',
              '&:focus': {
                borderColor: isError ? theme.palette.error.main : theme.palette.primary.main,
                background: isLight ? '#fff' : '#1C1B2E',
              },
            }}
          />
        ))}
      </Box>

      <AnimatePresence>
        {verified && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
            <Box
              sx={{
                textAlign: 'center',
                color: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <CheckCircleOutlineIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}>
        Didn&apos;t receive a code?{' '}
        {cooldownSecondsLeft > 0 ? (
          <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'text.disabled' }}>
            Resend in {cooldownSecondsLeft}s
          </Typography>
        ) : (
          <Link
            component="button"
            type="button"
            variant="caption"
            disabled={resendBusy || processing || verified}
            onClick={() => {
              void onResend();
            }}
            sx={{ fontWeight: 600, color: 'primary.main', textDecoration: 'none' }}
          >
            {resendBusy ? 'Resending…' : resendLabel}
          </Link>
        )}
      </Typography>
    </Box>
  );
}
